<template>
  <canvas
    ref="canvasRef"
    aria-hidden="true"
    class="text-onaction-default-hi font-body-1 pointer-events-none absolute z-0 h-[calc(100%+16px)] w-[calc(100%+16px)]"
    :style="snowStyle"
    data-test="shopping-assistant-fab-snow"
  />
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useIsShoppingAssistantFabVisible } from '../../composables/useIsShoppingAssistantFabVisible'

import {
  DEFAULT_SNOWFALL_OPTIONS,
  SnowfallEngine,
} from './snowfall/snowfallEngine'

// The snow layer extends 8px beyond the button on every side and clips
// there, per the Figma "Festive FAB" component (Frame 1 is 128×74 behind a
// 112×56 FAB, offset -8/-8).
const SNOW_OVERSIZED_PX = 8

// Slow the snowfall down while the FAB is hovered, per the Figma dev
// annotation ("slow down .5x on hover").
const HOVER_SLOWDOWN = 0.5

const snowStyle = {
  top: `${-SNOW_OVERSIZED_PX}px`,
  left: `${-SNOW_OVERSIZED_PX}px`,
  // Canvas is a replaced element: insets alone don't size it, so its
  // width/height classes do (calc(100% + 16px) via the template).
  // Clip the snow to the button's own rounded shape: the canvas box is
  // 8px overscanned per side, so inset(8px) restores the button box and
  // `round 12px` matches its `rounded-lg` radius (Figma "Festive FAB"
  // corner radius 12, corner smoothing 0.6).
  clipPath: `inset(${SNOW_OVERSIZED_PX}px round 12px)`,
}

const canvasRef = ref<HTMLCanvasElement | null>(null)
const isFabVisible = useIsShoppingAssistantFabVisible()

let engine: SnowfallEngine | null = null

// Hover targets the FAB button (the canvas is pointer-events-none), so
// the hover listeners live on the canvas's parent element.
let host: HTMLElement | null = null
const onEnter = () => engine?.setHovered(true)
const onLeave = () => engine?.setHovered(false)

function resolveTokens() {
  // The canvas element itself carries the design-token probe classes
  // (`text-onaction-default-hi` for the glyph color, `font-body-1` for the
  // BM Duplet font family): reading its computed style resolves the
  // mood-aware CSS custom properties into concrete values for the 2D
  // context, which can't consume `var(...)` directly. A canvas ignores
  // text styling itself, so the probe classes have no visual side effect.
  const computed = canvasRef.value
    ? window.getComputedStyle(canvasRef.value)
    : null

  return {
    color: computed?.color || DEFAULT_SNOWFALL_OPTIONS.color,
    fontFamily: computed?.fontFamily || DEFAULT_SNOWFALL_OPTIONS.fontFamily,
  }
}

// Pause the rAF loop while the FAB is v-show-hidden (e.g. the assistant
// panel is open) — no main-thread wakeups for an invisible layer. The
// engine's own ResizeObserver re-syncs size and flakes once visible again.
watch(isFabVisible, (visible) => {
  if (!engine || engine.isReducedMotion()) return

  if (visible) {
    engine.start()
  } else {
    engine.stop()
  }
})

onMounted(() => {
  const canvas = canvasRef.value
  if (!canvas) return

  const { color, fontFamily } = resolveTokens()

  engine = new SnowfallEngine(canvas, {
    ...DEFAULT_SNOWFALL_OPTIONS,
    color,
    fontFamily,
    // Half speed while hovered, per the Figma dev annotation.
    hoverSlowdown: HOVER_SLOWDOWN,
  })

  host = canvas.parentElement
  host?.addEventListener('pointerenter', onEnter)
  host?.addEventListener('pointerleave', onLeave)

  if (isFabVisible.value) {
    engine.start()
  }
})

onBeforeUnmount(() => {
  host?.removeEventListener('pointerenter', onEnter)
  host?.removeEventListener('pointerleave', onLeave)

  engine?.destroy()
  engine = null
})
</script>
